import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { MobileLayout } from "@/components/Layout";
import { toast } from "sonner";
import {
  Plus,
  FolderKanban,
  MapPin,
  User,
  Calendar,
  ChevronRight,
  Loader2,
  Archive
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const ProjectsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("actif");
  const [formData, setFormData] = useState({
    company_id: "",
    name: "",
    address: "",
    client: "",
    start_date: "",
    description: ""
  });

  useEffect(() => { loadData(); }, [tab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/projects?status=${tab}`);
      setProjects(res.data);
    } catch (error) {
      console.error("Error loading projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...formData, company_id: user?.company_id || formData.company_id };
      await api.post("/projects", payload);
      toast.success("Projet cree");
      setShowModal(false);
      setFormData({ company_id: "", name: "", address: "", client: "", start_date: "", description: "" });
      if (tab === "actif") loadData();
      else setTab("actif");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la creation");
    } finally {
      setSaving(false);
    }
  };

  const openModal = () => {
    if (user?.company_id) setFormData(prev => ({ ...prev, company_id: user.company_id }));
    setShowModal(true);
  };

  const isArchive = tab === "archivé";

  return (
    <MobileLayout title="Projets">
      <div className="px-4 lg:px-6 py-5 space-y-4 animate-fade-in">
        {/* Tabs */}
        <div className="flex gap-2" data-testid="project-tabs">
          <button
            onClick={() => setTab("actif")}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              tab === "actif" ? "bg-zinc-800 text-white" : "text-zinc-500 hover:text-zinc-300"
            }`}
            data-testid="tab-actif"
          >
            <FolderKanban size={16} /> Actifs
          </button>
          <button
            onClick={() => setTab("archivé")}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              tab === "archivé" ? "bg-zinc-800 text-white" : "text-zinc-500 hover:text-zinc-300"
            }`}
            data-testid="tab-archive"
          >
            <Archive size={16} /> Chantiers archivés
          </button>
        </div>

        {/* Count */}
        <p className="text-zinc-400 text-sm" data-testid="project-count">
          {projects.length} projet{projects.length !== 1 ? "s" : ""} {isArchive ? "archivé(s)" : "actif(s)"}
        </p>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="spinner"></div></div>
        ) : projects.length === 0 ? (
          <div className="empty-state py-12">
            {isArchive ? (
              <>
                <Archive size={48} className="mx-auto mb-4 text-zinc-600" />
                <p className="text-zinc-400">Aucun chantier archivé</p>
              </>
            ) : (
              <>
                <FolderKanban size={48} className="mx-auto mb-4 text-zinc-600" />
                <p className="text-zinc-400 mb-4">Aucun projet</p>
                {(user?.role === "admin_entreprise" || user?.role === "conducteur_travaux") && (
                  <Button onClick={openModal} className="btn-primary" data-testid="create-first-project">
                    <Plus size={18} className="mr-2" /> Creer un projet
                  </Button>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0">
            {projects.map((project) => (
              <button
                key={project.id}
                onClick={() => navigate(`/projects/${project.id}`)}
                className={`card-interactive w-full p-4 text-left ${isArchive ? "opacity-60" : ""}`}
                data-testid={`project-item-${project.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-semibold text-lg truncate">{project.name}</h3>
                      {isArchive && (
                        <span className="text-xs px-2 py-0.5 rounded border bg-zinc-700/50 text-zinc-400 border-zinc-600 whitespace-nowrap" data-testid={`badge-archive-${project.id}`}>
                          Archivé
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-zinc-400 text-sm">
                      <MapPin size={14} />
                      <span className="truncate">{project.address}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1 text-zinc-500">
                        <User size={14} />
                        <span>{project.client}</span>
                      </div>
                      <div className="flex items-center gap-1 text-zinc-500">
                        <Calendar size={14} />
                        <span>{project.start_date}</span>
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-zinc-600 flex-shrink-0 mt-1" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* FAB — only for active tab and manage roles */}
      {!isArchive && (user?.role === "admin_entreprise" || user?.role === "conducteur_travaux") && (
        <button onClick={openModal} className="fab" data-testid="add-project-fab">
          <Plus size={24} className="text-white" />
        </button>
      )}

      {/* Create Project Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Nouveau projet</DialogTitle>
            <p className="text-zinc-400 text-sm">Remplissez les informations du projet</p>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm uppercase tracking-wider">Nom du projet</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Residence Les Pins" required className="input-dark" data-testid="project-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm uppercase tracking-wider">Adresse</Label>
              <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="12 Rue des Pins, 75001 Paris" required className="input-dark" data-testid="project-address-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm uppercase tracking-wider">Client</Label>
              <Input value={formData.client} onChange={(e) => setFormData({ ...formData, client: e.target.value })} placeholder="M. Dupont" required className="input-dark" data-testid="project-client-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm uppercase tracking-wider">Date de debut</Label>
              <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required className="input-dark" data-testid="project-date-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300 text-sm uppercase tracking-wider">Description</Label>
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Description du projet..." rows={3} className="input-dark w-full resize-none py-3" data-testid="project-description-input" />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Annuler</Button>
              <Button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="project-submit-btn">
                {saving ? <Loader2 className="animate-spin" size={20} /> : "Creer"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </MobileLayout>
  );
};

export default ProjectsPage;
